import { formatPrice } from './money-utils';

/** Loose filter shape: the API sends numbers, the editor form holds strings. */
export interface DescribableFilters {
  minRent?: number | string | null;
  maxRent?: number | string | null;
  minBaseRent?: number | string | null;
  maxBaseRent?: number | string | null;
  minRooms?: number | string | null;
  maxRooms?: number | string | null;
  minSpace?: number | string | null;
  maxSpace?: number | string | null;
  wbsFilter?: string | null;
  wgFilter?: string | null;
  swapFilter?: string | null;
  tempFilter?: string | null;
  providerFilter?: string | null;
  furnishedFilter?: string | null;
  closedAreaFilter?: string | null;
}

function num(v: number | string | null | undefined): number | null {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function range(min: number | null, max: number | null, fmt: (n: number) => string, unit = ''): string | null {
  if (min != null && max != null) return `${fmt(min)}–${fmt(max)}${unit}`;
  if (min != null) return `ab ${fmt(min)}${unit}`;
  if (max != null) return `bis ${fmt(max)}${unit}`;
  return null;
}

const rooms = (n: number) => (Number.isInteger(n) ? String(n) : n.toLocaleString('de-DE', { maximumFractionDigits: 1 }));
const plain = (n: number) => n.toLocaleString('de-DE');

/** Core facts in fact-line order: warm rent, cold rent, rooms, area — full ranges, missing ones skipped. */
export function describeCoreFilters(f: DescribableFilters | null | undefined): string[] {
  if (!f) return [];
  const items: string[] = [];
  const warm = range(num(f.minRent), num(f.maxRent), formatPrice);
  if (warm) items.push(`${warm} warm`);
  const cold = range(num(f.minBaseRent), num(f.maxBaseRent), formatPrice);
  if (cold) items.push(`${cold} kalt`);
  const r = range(num(f.minRooms), num(f.maxRooms), rooms, ' Zi.');
  if (r) items.push(r);
  const a = range(num(f.minSpace), num(f.maxSpace), plain, ' m²');
  if (a) items.push(a);
  return items;
}

/** The yes/no listing filters (WBS, WG, …) that differ from "egal", as short labels. */
export function describeFlagFilters(f: DescribableFilters | null | undefined): string[] {
  if (!f) return [];
  const items: string[] = [];
  const flag = (value: string | null | undefined, only: string, none: string) => {
    if (value === 'ONLY') items.push(only);
    else if (value === 'NONE') items.push(none);
  };
  flag(f.wbsFilter, 'nur mit WBS', 'ohne WBS');
  flag(f.wgFilter, 'nur WG', 'keine WG');
  flag(f.swapFilter, 'nur Tausch', 'kein Tausch');
  flag(f.tempFilter, 'nur befristet', 'unbefristet');
  flag(f.furnishedFilter, 'nur möbliert', 'unmöbliert');
  if (f.providerFilter === 'COMMERCIAL') items.push('nur gewerblich');
  else if (f.providerFilter === 'PRIVATE') items.push('nur privat');
  if (f.closedAreaFilter === 'ONLY') items.push('nur Abo-Bereiche');
  else if (f.closedAreaFilter === 'ANY') items.push('inkl. Abo-Bereiche');
  return items;
}
